import React, { useRef, useState } from 'react';
import {
  CustomWordlistData,
  parseCustomWordlist,
  saveCustomWordlist,
  removeCustomWordlist,
} from './wordfinderUtils';

interface CustomWordlistDialogProps {
  existingWordlist: CustomWordlistData | null;
  onClose: () => void;
  onWordlistUploaded: (wordlist: CustomWordlistData) => void;
  onWordlistCleared: () => void;
}

const CustomWordlistDialog: React.FC<CustomWordlistDialogProps> = ({
  existingWordlist,
  onClose,
  onWordlistUploaded,
  onWordlistCleared,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      alert('Please select a .txt file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        const content = e.target?.result as string;

        try {
          const customData = parseCustomWordlist(
            content,
            file.name,
            (progress) => setUploadProgress(progress)
          );

          await saveCustomWordlist(customData);
          onWordlistUploaded(customData);

          // Clear file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Error parsing wordlist:', error);
          alert(error instanceof Error ? error.message : 'Error parsing wordlist');
          setUploading(false);
          setUploadProgress(0);
        }
      };

      reader.onerror = () => {
        alert('Error reading file');
        setUploading(false);
        setUploadProgress(0);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClearWordlist = async () => {
    if (!existingWordlist) return;

    const confirmed = window.confirm(
      `Are you sure you want to clear the custom wordlist "${existingWordlist.name}"? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await removeCustomWordlist();
      onWordlistCleared();
    } catch (error) {
      console.error('Error clearing wordlist:', error);
      alert('Error clearing wordlist');
    }
  };

  return (
    <div className="wordfinderDialogOverlay" onClick={() => !uploading && onClose()}>
      <div className="wordfinderDialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="wordfinderDialogTitle">
          {existingWordlist ? 'Replace Custom Wordlist' : 'Upload Custom Wordlist'}
        </h2>
        <div className="wordfinderDialogInstructions">
          <p>Select a .txt file from your computer with one word per line.</p>
          <p>
            <strong>Optional:</strong> Include quality ratings in the format <code>word;rating</code>
            {' '}(e.g., <code>apple;85</code>)
          </p>
        </div>
        <div className="wordfinderDialogInfo">
          <strong>Privacy Notice:</strong> Your wordlist is stored locally in your browser only.
          It is never uploaded to any server nor shared in any way.
        </div>
        {existingWordlist && (
          <div className="wordfinderDialogWarning">
            <strong>Note:</strong> This will replace your current wordlist{' '}
            <em>{existingWordlist.name}</em> ({existingWordlist.wordCount.toLocaleString()} words).
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt"
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ marginTop: '16px' }}
        />
        {uploading && (
          <div className="wordfinderDialogProgress">
            <div className="wordfinderProgressBar">
              <div
                className="wordfinderProgressFill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p>Processing... {uploadProgress}%</p>
          </div>
        )}
        <div className="wordfinderDialogButtons">
          {existingWordlist && (
            <button
              type="button"
              onClick={handleClearWordlist}
              disabled={uploading}
              className="wordfinderClearButton"
            >
              Clear Wordlist
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomWordlistDialog;
